import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { Popup } from "../../utils/Div";
import {
  fetchPhoneroPersons,
  syncPhoneroPersons,
  updatePhoneroPerson,
  fetchPhoneroRoles,
} from "../../../api/company";

const PersonsManager = () => {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState({
    connected: false,
    message: "",
  });
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [editForm, setEditForm] = useState({
    firstname: "",
    lastname: "",
    email: "",
    function: "",
    department_id: "",
    role_id: "",
    field1: "",
    field2: "",
    tags: [],
  });
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  useEffect(() => {
    loadPersons();
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setRolesLoading(true);
    try {
      const response = await fetchPhoneroRoles();

      if (response.data?.success) {
        // Handle the correct API response format
        const rolesData = response.data.roles;

        if (rolesData && !rolesData.error && Array.isArray(rolesData.result)) {
          // Transform the API format to our expected format
          const transformedRoles = rolesData.result.map((role) => ({
            id: role.roleId,
            name: role.description,
          }));

          setRoles(transformedRoles);
        } else if (Array.isArray(rolesData)) {
          setRoles(rolesData);
        } else {
          console.warn("Unexpected roles response format:", rolesData);
          // Fallback to hardcoded roles
          setRoles([
            { id: 1, name: "Bruker" },
            { id: 2, name: "Avdelingsleder" },
            { id: 3, name: "Administrator" },
          ]);
        }
      } else {
        console.error("Failed to load roles:", response.data?.message);
        // Fallback to hardcoded roles if API fails
        setRoles([
          { id: 1, name: "Bruker" },
          { id: 2, name: "Avdelingsleder" },
          { id: 3, name: "Administrator" },
        ]);
      }
    } catch (err) {
      console.error("Roles load error:", err);
      // Fallback to hardcoded roles if API fails
      setRoles([
        { id: 1, name: "Bruker" },
        { id: 2, name: "Avdelingsleder" },
        { id: 3, name: "Administrator" },
      ]);
    } finally {
      setRolesLoading(false);
    }
  };

  const loadPersons = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchPhoneroPersons();

      if (response.data?.success) {
        // The API returns { error: false, totalCount: X, result: [...] }
        // The backend wraps it as { success: true, persons: { error: false, totalCount: X, result: [...] } }
        const personsData = response.data.persons?.result || [];

        setPersons(personsData);

        // Update sync status based on whether we got persons data
        if (personsData.length > 0) {
          const connectedCount = personsData.filter(
            (p) => p.sync_status === "connected"
          ).length;
          const totalCount = personsData.length;

          setSyncStatus({
            connected: true,
            message: `Loaded ${totalCount} persons from Phonero (${connectedCount} connected to Promang users)`,
          });
        } else {
          setSyncStatus({
            connected: false,
            message: "No persons found in Phonero system",
          });
        }
      } else {
        setError(response.data?.message || "Failed to load persons");
        setSyncStatus({
          connected: false,
          message: "Failed to connect to Phonero system",
        });
      }
    } catch (err) {
      setError("Failed to load persons");
      setSyncStatus({
        connected: false,
        message: "Failed to connect to Phonero system",
      });
      console.error("Persons load error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncPersons = async () => {
    setSyncing(true);
    setError(null);

    try {
      const response = await syncPhoneroPersons();

      if (response.data?.success) {
        // Sync job was started successfully
        setSyncStatus({
          connected: true,
          message: response.data.message || "Person sync started in background",
        });

        // Clear any previous errors since sync was initiated successfully
        setError(null);

        // Wait a moment then reload persons to see if any connections were made
        setTimeout(async () => {
          await loadPersons();
        }, 2000);
      } else {
        // Check if it's a specific error or just no data
        const errorMessage = response.data?.message || "Failed to start sync";
        setError(errorMessage);

        // Update sync status based on the error
        setSyncStatus({
          connected: false,
          message: errorMessage,
        });
      }
    } catch (err) {
      console.error("Persons sync error:", err);

      // Check if it's a network error or API error
      if (err.response) {
        // API responded with error status
        setError(
          `Sync failed: ${err.response.data?.message || "Unknown error"}`
        );
        setSyncStatus({
          connected: false,
          message: "Failed to connect to Phonero system",
        });
      } else if (err.request) {
        // Network error
        setError("Network error - please check your connection");
        setSyncStatus({
          connected: false,
          message: "Network connection failed",
        });
      } else {
        // Other error
        setError("Failed to sync persons");
        setSyncStatus({
          connected: false,
          message: "Sync operation failed",
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleViewDetails = (person) => {
    setSelectedPerson(person);
    setDetailsDialogOpen(true);
  };

  const handleEditPerson = async (person) => {
    // Ensure roles are loaded before opening the dialog
    if (!Array.isArray(roles) || roles.length === 0) {
      await loadRoles();
    }

    setEditingPerson(person);
    setEditForm({
      firstname: person.firstname || "",
      lastname: person.lastname || "",
      email: person.email || "",
      function: person.function || "",
      department_id:
        person.departmentId && person.departmentId > 0
          ? person.departmentId.toString()
          : "",
      role_id: person.roleId && person.roleId > 0 ? person.roleId : "",
      field1: person.field1 || "",
      field2: person.field2 || "",
      tags: person.tags || [],
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingPerson) return;

    setLoading(true);
    setError(null);

    try {
      // Clean up the form data before sending
      const cleanFormData = {
        ...editForm,
        department_id:
          editForm.department_id && editForm.department_id.trim() !== ""
            ? parseInt(editForm.department_id)
            : null,
        role_id:
          editForm.role_id && editForm.role_id.trim() !== ""
            ? parseInt(editForm.role_id)
            : null,
      };

      const response = await updatePhoneroPerson(
        editingPerson.personId,
        cleanFormData
      );

      if (response.data?.success) {
        setEditDialogOpen(false);
        await loadPersons(); // Refresh the data
      } else {
        setError(response.data?.message || "Failed to update person");
      }
    } catch (err) {
      setError("Failed to update person");
      console.error("Update person error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getNumberTypeColor = (type) => {
    switch (type) {
      case "MOBILE":
        return "primary";
      case "WORK":
        return "secondary";
      default:
        return "default";
    }
  };

  const getPresenceStatusColor = (presenceId) => {
    switch (presenceId) {
      case 1:
        return "success";
      case 2:
        return "warning";
      case 3:
        return "error";
      default:
        return "default";
    }
  };

  const getRoleName = (roleId) => {
    if (!roleId) return "No role";
    if (!Array.isArray(roles)) return `Role ${roleId}`;
    const role = roles.find((r) => r.id === roleId);
    return role ? role.name : `Role ${roleId}`;
  };

  if (loading && persons.length === 0) {
    return (
      <Card>
        <CardContent style={{ textAlign: "center", padding: "2rem" }}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginTop: "1rem" }}>
            Loading persons data...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <CardContent>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            style={{ marginBottom: "1rem" }}
          >
            <Typography
              variant="h6"
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              Persons Management ({persons.length})
              <Popup content={syncStatus.message}>
                {syncStatus.connected ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                ) : (
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600" />
                )}
              </Popup>
            </Typography>
            <div>
              <Button
                variant="outlined"
                onClick={loadPersons}
                disabled={loading}
                style={{ marginRight: "0.5rem" }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                onClick={handleSyncPersons}
                disabled={syncing}
                startIcon={syncing ? <CircularProgress size={16} /> : null}
              >
                {syncing ? "Connecting..." : "Connect Users to Phonero"}
              </Button>
            </div>
          </Grid>

          {error && (
            <Alert severity="error" style={{ marginBottom: "1rem" }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Function</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Numbers</TableCell>
                  <TableCell>Presence</TableCell>
                  <TableCell>Sync Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {persons.map((person) => (
                  <TableRow key={person.personId}>
                    <TableCell>
                      <Typography variant="body2">
                        {person.firstname} {person.lastname}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {person.personId}
                      </Typography>
                    </TableCell>
                    <TableCell>{person.username}</TableCell>
                    <TableCell>{person.email}</TableCell>
                    <TableCell>{person.function}</TableCell>
                    <TableCell>{person.department}</TableCell>
                    <TableCell>{getRoleName(person.roleId)}</TableCell>
                    <TableCell>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "0.25rem",
                        }}
                      >
                        {person.numbers?.map((number, index) => (
                          <Chip
                            key={index}
                            label={`${number.number} (${number.type})`}
                            size="small"
                            color={getNumberTypeColor(number.type)}
                            variant="outlined"
                          />
                        )) || "No numbers"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={person.presenceDescription || "Unknown"}
                        size="small"
                        color={getPresenceStatusColor(person.presenceId)}
                      />
                    </TableCell>
                    <TableCell>
                      <Popup
                        content={
                          person.sync_status === "connected"
                            ? `Connected to: ${person.connected_user_name} (${person.connected_user_email})`
                            : "Not connected to any Promang user"
                        }
                      >
                        <Chip
                          label={
                            person.sync_status === "connected"
                              ? "Connected"
                              : "Not Connected"
                          }
                          size="small"
                          color={
                            person.sync_status === "connected"
                              ? "success"
                              : "error"
                          }
                          icon={
                            person.sync_status === "connected" ? (
                              <CheckCircleIcon style={{ fontSize: "12px" }} />
                            ) : (
                              <ExclamationCircleIcon
                                style={{ fontSize: "12px" }}
                              />
                            )
                          }
                        />
                      </Popup>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewDetails(person)}
                        >
                          Details
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleEditPerson(person)}
                        >
                          Edit
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Person Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Person Details: {selectedPerson?.firstname} {selectedPerson?.lastname}
        </DialogTitle>
        <DialogContent>
          {selectedPerson && (
            <Grid container spacing={2} style={{ marginTop: "0.5rem" }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Basic Information
                </Typography>
                <TextField
                  fullWidth
                  label="Person ID"
                  value={selectedPerson.personId}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Username"
                  value={selectedPerson.username}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="First Name"
                  value={selectedPerson.firstname}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={selectedPerson.lastname}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={selectedPerson.email}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Work Information
                </Typography>
                <TextField
                  fullWidth
                  label="Function"
                  value={selectedPerson.function}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Department"
                  value={selectedPerson.department}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Department ID"
                  value={selectedPerson.departmentId}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Role"
                  value={getRoleName(selectedPerson.roleId)}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Role ID"
                  value={selectedPerson.roleId}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Phone Numbers
                </Typography>
                <div
                  style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}
                >
                  {selectedPerson.numbers?.map((number, index) => (
                    <Card
                      key={index}
                      variant="outlined"
                      style={{ padding: "0.5rem" }}
                    >
                      <Typography variant="body2">
                        <strong>{number.number}</strong>
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Type: {number.type}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="textSecondary">
                        State: {number.state}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="textSecondary">
                        Direction: {number.direction}
                      </Typography>
                    </Card>
                  )) || "No phone numbers"}
                </div>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Presence Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Presence ID"
                      value={selectedPerson.presenceId}
                      InputProps={{ readOnly: true }}
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Presence Description"
                      value={selectedPerson.presenceDescription}
                      InputProps={{ readOnly: true }}
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Presence Text"
                      value={selectedPerson.presenceText}
                      InputProps={{ readOnly: true }}
                      margin="dense"
                    />
                  </Grid>
                </Grid>
              </Grid>

              {selectedPerson.tags && selectedPerson.tags.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tags
                  </Typography>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}
                  >
                    {selectedPerson.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" />
                    ))}
                  </div>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Person Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Person: {editingPerson?.firstname} {editingPerson?.lastname}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: "0.5rem" }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Basic Information
              </Typography>
              <TextField
                fullWidth
                label="First Name"
                value={editForm.firstname}
                onChange={(e) =>
                  setEditForm({ ...editForm, firstname: e.target.value })
                }
                margin="dense"
              />
              <TextField
                fullWidth
                label="Last Name"
                value={editForm.lastname}
                onChange={(e) =>
                  setEditForm({ ...editForm, lastname: e.target.value })
                }
                margin="dense"
              />
              <TextField
                fullWidth
                label="Email"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
                margin="dense"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Work Information
              </Typography>
              <TextField
                fullWidth
                label="Function"
                value={editForm.function}
                onChange={(e) =>
                  setEditForm({ ...editForm, function: e.target.value })
                }
                margin="dense"
              />
              <TextField
                fullWidth
                label="Department ID"
                value={editForm.department_id}
                onChange={(e) =>
                  setEditForm({ ...editForm, department_id: e.target.value })
                }
                margin="dense"
                type="number"
                helperText="Leave empty if no department is assigned"
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Role</InputLabel>
                <Select
                  key={`role-select-${editForm.role_id}`}
                  value={editForm.role_id ? editForm.role_id.toString() : ""}
                  onChange={(e) => {
                    setEditForm({
                      ...editForm,
                      role_id: e.target.value ? parseInt(e.target.value) : "",
                    });
                  }}
                  label="Role"
                  disabled={rolesLoading}
                  displayEmpty
                >
                  {rolesLoading ? (
                    <MenuItem disabled>
                      <em>Loading roles...</em>
                    </MenuItem>
                  ) : (
                    <>
                      <MenuItem value="">
                        <em>No role assigned</em>
                      </MenuItem>
                      {Array.isArray(roles) &&
                        roles.map((role) => {
                          const roleValue = role.id.toString();
                          const currentValue = editForm.role_id
                            ? editForm.role_id.toString()
                            : "";

                          return (
                            <MenuItem key={role.id} value={roleValue}>
                              {role.name}
                            </MenuItem>
                          );
                        })}
                    </>
                  )}
                </Select>
                {rolesLoading && (
                  <CircularProgress
                    size={20}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Additional Fields
              </Typography>
              <TextField
                fullWidth
                label="Field 1"
                value={editForm.field1}
                onChange={(e) =>
                  setEditForm({ ...editForm, field1: e.target.value })
                }
                margin="dense"
              />
              <TextField
                fullWidth
                label="Field 2"
                value={editForm.field2}
                onChange={(e) =>
                  setEditForm({ ...editForm, field2: e.target.value })
                }
                margin="dense"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Tags (comma-separated)
              </Typography>
              <TextField
                fullWidth
                label="Tags"
                value={editForm.tags.join(", ")}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    tags: e.target.value.split(",").map((tag) => tag.trim()),
                  })
                }
                margin="dense"
                helperText="Enter tags separated by commas"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Person"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PersonsManager;
